/**
 * KPI dashboard (A-02): the period presets, and how figures, changes, ranks, durations and trend
 * points read. Pure, so the rules are unit-tested (tests/unit/kpi.test.mjs).
 *
 * Timestamps: the API sends client-zone (IST) wall time with its offset ("…T14:00:00.000+05:30"),
 * so labels read the date and time straight off the string — the same in any browser time zone.
 */

export const PERIODS = [
  { days: 1, label: "Today" },
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
] as const;

export const DEFAULT_DAYS = 30;

/** The period from a `?days=` value: one of the presets, else the default. */
export function parseDays(value: string | null): number {
  const n = Number(value);
  return PERIODS.some((p) => p.days === n) ? n : DEFAULT_DAYS;
}

export const MEDICINE_SORTS = [
  { value: "units", label: "Units", noun: "units sold" },
  { value: "revenue", label: "Revenue", noun: "revenue" },
  { value: "orders", label: "Orders", noun: "orders" },
] as const;

export type MedicineSort = (typeof MEDICINE_SORTS)[number]["value"];

/** The ranking from a `?sort=` value: units, revenue or orders (default units). */
export function parseMedicineSort(value: string | null): MedicineSort {
  return MEDICINE_SORTS.find((s) => s.value === value)?.value ?? "units";
}

/** What the comparison period is called: "yesterday", "the previous 7 days". */
export function previousLabel(days: number): string {
  return days === 1 ? "yesterday" : `the previous ${days} days`;
}

// ------------------------------------------------------------------ numbers

const WHOLE = new Intl.NumberFormat("en-IN");
const COMPACT = new Intl.NumberFormat("en-IN", { notation: "compact", maximumFractionDigits: 1 });

/** A count with Indian digit grouping: 1,284 · 1,23,456. */
export function formatNumber(n: number): string {
  return WHOLE.format(n);
}

/** A count at a glance: in full below 10,000, then compact (12.9K, 1.2L, 3.4Cr). */
export function formatCompact(n: number): string {
  return Math.abs(n) < 10_000 ? WHOLE.format(n) : COMPACT.format(n);
}

const trimZero = (s: string) => s.replace(/\.0$/, "");

/** A ratio as a percentage: 0.4286 → "42.9%", 1 → "100%". */
export function formatPercent(ratio: number, digits = 1): string {
  return `${trimZero((ratio * 100).toFixed(digits))}%`;
}

// ------------------------------------------------------------------ change vs the previous period

export type Trend = "up" | "down" | "flat";

export interface Change {
  trend: Trend;
  /** The relative change (0.12 = +12%); null when there was nothing before to compare with. */
  ratio: number | null;
}

export function change(current: number, previous: number): Change {
  if (current === previous) return { trend: "flat", ratio: 0 };
  const trend: Trend = current > previous ? "up" : "down";
  return { trend, ratio: previous === 0 ? null : (current - previous) / Math.abs(previous) };
}

/** "+12%", "−3.4%", "No change", "New" (up from nothing). Under 10% keeps one decimal. */
export function changeLabel(c: Change): string {
  if (c.trend === "flat") return "No change";
  if (c.ratio === null) return c.trend === "up" ? "New" : "−100%";
  const pct = Math.abs(c.ratio * 100);
  const text = pct < 10 ? trimZero(pct.toFixed(1)) : Math.round(pct).toString();
  return `${c.ratio > 0 ? "+" : "−"}${text}%`;
}

/** A rate's change in percentage points: 0.92 vs 0.88 → "+4 pts". */
export function pointsLabel(current: number, previous: number): string {
  const pts = (current - previous) * 100;
  if (Math.abs(pts) < 0.05) return "No change";
  return `${pts > 0 ? "+" : "−"}${trimZero(Math.abs(pts).toFixed(1))} pts`;
}

/** Whether a change reads as good, bad or neither, given which way is good for the figure. */
export function tone(trend: Trend, upIsGood: boolean): "good" | "bad" | "neutral" {
  if (trend === "flat") return "neutral";
  return (trend === "up") === upIsGood ? "good" : "bad";
}

// ------------------------------------------------------------------ ranks

export type RankMove = { kind: "new" } | { kind: "same" } | { kind: "up" | "down"; by: number };

/** How a rank moved since the previous period (a smaller number is a better rank). */
export function rankMove(rank: number, previous: number | null): RankMove {
  if (previous === null) return { kind: "new" };
  if (previous === rank) return { kind: "same" };
  return previous > rank ? { kind: "up", by: previous - rank } : { kind: "down", by: rank - previous };
}

/** Where a rank sits among `of`: "Best seller", "Top 5%" in the better half, "Bottom 12%" in the
 *  other. */
export function standing(rank: number, of: number): string {
  if (of <= 0) return "";
  if (rank === 1) return "Best seller";
  const share = rank / of;
  if (share <= 0.5) return `Top ${Math.max(1, Math.ceil(share * 100))}%`;
  return `Bottom ${Math.max(1, Math.ceil(((of - rank + 1) / of) * 100))}%`;
}

// ------------------------------------------------------------------ time

/** Hours as people say them: "35 min", "4.5 hr", "2.3 days"; "—" when unknown. */
export function formatHours(hours: number | null): string {
  if (hours === null) return "—";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 48) return `${trimZero(hours.toFixed(1))} hr`;
  return `${trimZero((hours / 24).toFixed(1))} days`;
}

/** How long since `iso`, in whole units: "12 min", "5 hr", "3 days" (a queue's wait). */
export function ageLabel(iso: string, now: number): string {
  const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 60) return `${Math.max(1, minutes)} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} hr`;
  return `${Math.floor(hours / 24)} days`;
}

/** The date ("2026-10-19") and time ("14:00") an API timestamp shows. */
export function wallClock(iso: string): { date: string; time: string } {
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-19" → "19 Oct" (with the year: "19 Oct 2026"). */
export function dayLabel(date: string, withYear = false): string {
  const [year, month, day] = date.split("-").map(Number);
  return `${day} ${MONTHS[month - 1]}${withYear ? ` ${year}` : ""}`;
}

/** The days between two API timestamps: "13 Oct – 19 Oct", "19 Oct" for one day; years are
 *  shown when the range crosses one. */
export function rangeLabel(fromIso: string, toIso: string): string {
  const from = wallClock(fromIso).date;
  const to = wallClock(toIso).date;
  if (from === to) return dayLabel(from);
  const crossesYear = from.slice(0, 4) !== to.slice(0, 4);
  return `${dayLabel(from, crossesYear)} – ${dayLabel(to, crossesYear)}`;
}

// ------------------------------------------------------------------ trend bars

/** Points in runs of `size`, counted back from the latest so the latest run is whole (the
 *  earliest may be shorter). A size of 1 keeps every point on its own. */
export function chunkFromEnd<T>(points: T[], size: number): T[][] {
  if (size <= 1) return points.map((p) => [p]);
  const runs: T[][] = [];
  for (let end = points.length; end > 0; end -= size) runs.unshift(points.slice(Math.max(0, end - size), end));
  return runs;
}

/** The run length that fits `count` points into at most `maxBars` bars. */
export function runLength(count: number, maxBars: number): number {
  return Math.max(1, Math.ceil(count / Math.max(1, Math.floor(maxBars))));
}

/** The x-axis label of a run of trend points: the hour ("14:00"), the day ("19 Oct") or the
 *  days it spans ("13 – 19 Oct", "28 Sep – 4 Oct"). */
export function runLabel(starts: string[], bucket: "hour" | "day"): string {
  const first = wallClock(starts[0]);
  if (bucket === "hour") return first.time;
  if (starts.length === 1) return dayLabel(first.date);
  const last = wallClock(starts[starts.length - 1]).date;
  const [from, to] = [dayLabel(first.date), dayLabel(last)];
  return from.split(" ")[1] === to.split(" ")[1] ? `${from.split(" ")[0]} – ${to}` : `${from} – ${to}`;
}

/** Evenly spaced axis ticks from 0 up to a round number at or above `max`. `integer`: whole
 *  steps only, for counts (no "2.5 orders"). */
export function niceTicks(max: number, { count = 4, integer = false } = {}): number[] {
  if (max <= 0) return [0, 1];
  const rough = max / count;
  const magnitude = 10 ** Math.floor(Math.log10(rough));
  const multiples = integer ? [1, 2, 5, 10] : [1, 2, 2.5, 5, 10];
  const nice = multiples.map((m) => m * magnitude).find((s) => s >= rough) ?? 10 * magnitude;
  const step = integer ? Math.max(1, Math.round(nice)) : nice;
  const ticks: number[] = [];
  for (let v = 0; v < max + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  if (ticks[ticks.length - 1] < max) ticks.push(Number((ticks[ticks.length - 1] + step).toPrecision(12)));
  return ticks;
}
